import { ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { JupiterTabHeaderComponent } from './header/header.component';
import { DisplayGrid, GridsterModule } from 'angular-gridster2';
import { TabLayout, Entity, TemplateTab } from '../../types';
import { filter, noop, Observable, tap } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SaveTemplateModalResult } from '@shared/components/widgets/save-template/save-template.types';
import { AsyncPipe, CommonModule } from '@angular/common';
import { JupiterState } from '../../@store/state';
import { MatIconModule } from '@angular/material/icon';
import { JupiterPanelHostComponent } from '../../@components/jupiter-panel-host';
import { DataUpdatedArgs, EditToggle, JupiterPanelDef, Origin } from '../../@components/panels/types';
import { ofActionCompleted } from '@ngxs/store';
import { TsComponent } from '../../@components/ts.component';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { TsButtonModule, TSIconModule } from 'tsui';
import { gridsterConfig } from '../../consts';
import { EntityActions, TemplateActions } from '../../@store/actions';
import { FormPanelComponent } from '../../@components/panels/form/form.component';
import { LayoutSaveModalComponent } from '../../@components/layout-save-modal/layout-save-modal.component';
import { JupiterDashboardLoadingComponent } from '../../splash/dashboard-loading/dashboard-loading.component';
import { PanelMatrixComponent } from '@shared/components/panels/matrix/matrix.component';

@Component({
  selector: 'jupiter-tab',
  standalone: true,
  imports: [
    JupiterTabHeaderComponent,
    AsyncPipe,
    CommonModule,
    MatIconModule,
    GridsterModule,
    JupiterPanelHostComponent,
    MatLegacyButtonModule,
    TSIconModule,
    TsButtonModule,
    JupiterDashboardLoadingComponent,
  ],
  templateUrl: './tab.component.html',
  styleUrl: './tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterTabComponent extends TsComponent implements OnDestroy {
  @ViewChildren('panelHosts') panelHosts: QueryList<JupiterPanelHostComponent>;

  zone = inject(NgZone);

  entity$: Observable<any> = this.store.select(JupiterState.getEntity).pipe(
    tap((entity) => {
      this.stateEntity = entity;
    }),
  );

  relatedTo$: Observable<any> = this.store.select(JupiterState.getRelatedTo);
  data$ = this.activatedRoute.data;

  gridOptions = {
    ...gridsterConfig,
    displayGrid: DisplayGrid.OnDragAndResize,
    draggable: { enabled: false },
    resizable: { enabled: false },
  };

  ui = {
    editMode: false,
    isLoading: true,
    panelIdInEditMode: null,
  };

  changeTokenPair: { state: string; newState: string };
  tabs: TemplateTab[] = [];
  tabsMemento: TemplateTab[] = [];
  layout: TabLayout[] = [];
  layoutMemento: TabLayout[] = [];
  tabId?: string;

  protected stateEntity: Entity;
  private lastBusyPanelId: string = null;
  public get hasLayoutChange(): boolean {
    return this.changeTokenPair.state !== this.changeTokenPair.newState;
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          this.tabId = params.tabId;
          this.layout = this.tabs.find(({ id }) => id === this.tabId)?.layout ?? [];
        }),
      )
      .subscribe(noop);

    this.store
      .select(JupiterState.getEntityTabs)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((tabs) => {
          this.tabs = tabs;
          this.tabId = this.tabId ?? tabs[0]?.id;
          this.layout = tabs.find(({ id }) => id === this.tabId)?.layout ?? [];
          this.layoutMemento = JSON.parse(JSON.stringify(this.layout));
          this.tabsMemento = JSON.parse(JSON.stringify(this.tabs));
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.store
      .select(JupiterState.getEntity)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((x) => !!x),
      )
      .subscribe((entity) => {
        if (entity.template.definition.tabs?.length) {
          this.router
            .navigate(['../', this.tabId], { relativeTo: this.activatedRoute })
            .then(() => (this.ui = { ...this.ui, isLoading: false }));
        } else {
          throw new Error('No definition or tab found');
        }
      });

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(TemplateActions.Update),
        tap(() => {
          this.ui = { ...this.ui, isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(EntityActions.Update, EntityActions.ImageActions.Add, EntityActions.ImageActions.Delete),
        tap(() => {
          const panel = this.panelHosts.find((x) => x.panelDef.id === this.lastBusyPanelId);

          if (panel) {
            panel.componentInstance.isBusy.next(false);
            panel.componentInstance.touched.next(false);
            this.lastBusyPanelId = null;

            this.reloadFormBasedPanels();
          }
        }),
      )
      .subscribe(noop);
  }

  ngOnDestroy(): void {
    this.reloadFormBasedPanels();
  }

  onPanelAdded(panelDef: JupiterPanelDef<any>): void {
    this.layout = [...this.layout, { x: 0, y: 0, cols: 1, rows: 2, panel: { ...panelDef, id: this.rndId() } }];
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onPanelDefUpdated(panelDef: JupiterPanelDef<any>): void {
    this.matDialog
      .open(LayoutSaveModalComponent, {
        autoFocus: false,
        data: {
          templateName: this.stateEntity.template.name,
          typeLabel: this.store.selectSnapshot(
            ObjectsManagerState.getByName(this.store.selectSnapshot(JupiterState.getRelatedTo)),
          ).label,
        },
      })
      .afterClosed()
      .pipe(
        tap((result: SaveTemplateModalResult) => {
          if (result) {
            const tab = this.stateEntity.template.definition.tabs.find(({ id }) => this.tabId === id);
            const layoutItem = tab.layout.find(({ panel: { id } }) => panelDef.id === id);
            layoutItem.panel = { ...panelDef };

            if (result.saveMode === 'update') {
              this.store.dispatch(new TemplateActions.Update(this.stateEntity.template));
            } else if (result?.saveMode === 'create') {
              const payload = JSON.parse(JSON.stringify(this.stateEntity.template));
              delete payload.id;
              delete payload.rank;
              payload.name = result.templateName;
              this.store.dispatch(new TemplateActions.Create(payload));
            }
          } else {
            this.layout = JSON.parse(JSON.stringify(this.layoutMemento));
            this.tabs = JSON.parse(JSON.stringify(this.tabsMemento));
            this.panelHosts.find((x) => x.panelDef.id === panelDef.id).changeDetectorRef.markForCheck();
          }
        }),
      )
      .subscribe(noop);
  }

  onRemovePanel(item: TabLayout): void {
    this.layout = [...this.layout.filter(({ panel: { id } }) => item.panel.id !== id)];
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onTabAdded(tab: TemplateTab): void {
    const tabModel: TemplateTab = { ...tab, id: this.rndId() };
    this.tabs = [...this.tabs, { ...tabModel, layout: [] }];
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onTabUpdated(tab: TabLayout): void {
    const tabModel = this.tabs.find(({ id }) => tab.id === id);
    tabModel.name = tab.name;
    this.tabs = [...this.tabs];
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onTabRemoved(tab: TabLayout): void {
    this.tabs = [...this.tabs.filter(({ id }) => id !== tab.id)];
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onTabMoved(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  onTabChange(tabId: string): void {
    this.ui = { ...this.ui, isLoading: true, panelIdInEditMode: null };
    this.tabId = tabId;
    this.store.dispatch(new EntityActions.Load(this.stateEntity.id));
  }

  onToggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.gridOptions = {
      ...this.gridOptions,
      resizable: { ...this.gridOptions.resizable, enabled: this.ui.editMode },
      draggable: { ...this.gridOptions.draggable, enabled: this.ui.editMode },
      displayGrid: this.ui.editMode ? DisplayGrid.Always : DisplayGrid.OnDragAndResize,
    };

    if (this.ui.editMode) {
      const state = this.rndId();
      this.changeTokenPair = { state, newState: state };
    } else {
      this.changeTokenPair = null;
    }

    if (this.gridOptions.api?.optionsChanged) {
      this.gridOptions.api.optionsChanged();
    }
  }

  onLayoutSaved(): void {
    if (!this.hasLayoutChange) {
      this.tsSnackbarService.open('No changes detected', null, null, { duration: 1000 });
      this.onToggleEditMode();
      return;
    }

    this.matDialog
      .open(LayoutSaveModalComponent, {
        autoFocus: false,
        data: {
          templateName: this.stateEntity.template.name,
          typeLabel: this.store.selectSnapshot(
            ObjectsManagerState.getByName(this.store.selectSnapshot(JupiterState.getRelatedTo)),
          ).label,
        },
      })
      .afterClosed()
      .pipe(
        tap((result: SaveTemplateModalResult) => {
          if (result) {
            const tab = this.tabs.find(({ id }) => this.tabId === id);

            tab.layout = this.layout;
            this.ui = { ...this.ui, isLoading: true };

            const payload = {
              ...this.stateEntity.template,
              definition: {
                ...this.stateEntity.template.definition,
                tabs: this.tabs,
              },
            };

            if (result.saveMode === 'update') {
              this.store.dispatch(new TemplateActions.Update(payload));
            } else if (result?.saveMode === 'create') {
              delete payload.id;
              delete payload.rank;
              payload.name = result.templateName;
              this.store.dispatch(new TemplateActions.Create(payload));
            }
          } else {
            this.layout = JSON.parse(JSON.stringify(this.layoutMemento));
            this.tabs = JSON.parse(JSON.stringify(this.tabsMemento));
          }
        }),
      )
      .subscribe(() => this.onToggleEditMode());
  }

  onLayoutCanceled(): void {
    if (this.hasLayoutChange) {
      this.layout = JSON.parse(JSON.stringify(this.layoutMemento));
      this.tabs = JSON.parse(JSON.stringify(this.tabsMemento));
    }

    this.onToggleEditMode();
  }

  onDataUpdated(updatedData: DataUpdatedArgs<any, any>): void {
    const { model, origin, data } = updatedData;
    this.lastBusyPanelId = origin.id;
    this.panelHosts.find((x) => x.panelDef.id === origin.id).componentInstance.isBusy.next(true);

    switch (origin.type) {
      case 'images':
        if (data.mode === 'delete') {
          this.store.dispatch(new EntityActions.ImageActions.Delete(data.id));
        } else {
          this.store.dispatch(new EntityActions.ImageActions.Add(model, data.file));
        }
        break;
      case 'form':
        this.store.dispatch(
          new EntityActions.Update(data.touchedFields.reduce((p, c) => ({ ...p, [c]: model[c] }), { id: model.id })),
        );
        break;
      default:
        this.store.dispatch(new EntityActions.Update(model));
        break;
    }
  }

  onEditToggled(event: EditToggle<any>): void {
    if (event.toggle) {
      // @ts-ignore
      // the following change is only need for matrix panel because it is not in the jpt standard yet
      this.ui = { ...this.ui, panelIdInEditMode: event.origin?.id ?? event.panelDef.id };
    } else {
      this.ui = { ...this.ui, panelIdInEditMode: null };
    }
  }

  onDiscardClicked(event: Origin<any>): void {
    const ref = this.panelHosts.find((x) => x.panelDef.id === event.origin.id);

    if (ref.componentInstance instanceof FormPanelComponent) {
      ref.componentInstance.formFields.forEach((formField) => formField.loadComponent());
      ref.loadComponent();
    }
  }

  gridsterItemChange(): void {
    this.changeTokenPair = { ...this.changeTokenPair, newState: this.rndId() };
  }

  private reloadFormBasedPanels(): void {
    this.panelHosts.forEach((ref) => {
      if (ref.componentInstance instanceof FormPanelComponent) {
        ref.componentInstance.formFields.forEach((formField) => formField.loadComponent());
        ref.loadComponent();
      }

      if (ref.componentInstance instanceof PanelMatrixComponent) {
        ref.loadComponent();
      }
    });
  }
}
